import React, {FC} from 'react';

import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {CancelToken} from '../../../../../libs/shared/helpers/src/utilities/asyncCancelHelpers';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {buildTagSearchUrl} from '../../../../helpers/models/tagHelpers';
import {CompanyModel} from '../../../../models/api/companyModel';
import {RefinementOptionModel} from '../../../../models/api/refinementOptionModel';
import {ApiSearchTags} from '../../../../models/api/searchTagModel';
import {TagBehaviorsEnum, TagModel} from '../../../../models/api/tagModel';
import {fetchApiAsync} from '../../../../services/http/http';
import {RefinedInboxParameters} from '../../../../store/panels/inboxes/parameters/inboxesParameters';
import {
  CellsRefinementOptionsBaseSearchDropdown,
  REFINEMENT_SEARCH_DROPDOWN_PAGINATION_LIMIT,
  RefinementSearchDropdownPaginationState,
} from './cellsRefinementOptionsBaseSearchDropdown';
import {
  CellsRefinementOptionsSearchTagsDropdownRow,
  SearchTagsDropdownItem,
  SearchTagsDropdownItemTypes,
} from './cellsRefinementOptionsSearchTagsDropdownRow';

/*
 * Props.
 */

interface CellsRefinementOptionsSearchTagsDropdownProps {
  company: CompanyModel;
  tagNamespaces: ReadonlyArray<string>;
  popoverProps: PopoverTransferProps;
  selectedRefinement: RefinementOptionModel | undefined;
  selectedTags: ReadonlyArray<TagModel> | undefined;
  parameters: RefinedInboxParameters;
  onSelectTag: HandlerOf<TagModel>;
  onDropdownNavigateBack: Handler;
  usingTagsSearchShortcut: boolean;
}

/*
 * Component.
 */

export const CellsRefinementOptionsSearchTagsDropdown: FC<CellsRefinementOptionsSearchTagsDropdownProps> = (
  props,
) => {
  const {
    popoverProps,
    selectedRefinement,
    selectedTags,
    parameters,
    onDropdownNavigateBack,
    usingTagsSearchShortcut,
    onSelectTag,
    company,
    tagNamespaces,
  } = props;

  return (
    <CellsRefinementOptionsBaseSearchDropdown<SearchTagsDropdownItem>
      popoverProps={popoverProps}
      selectedRefinement={selectedRefinement}
      parameters={parameters}
      renderRow={(items, _index, _data, searchQuery) =>
        renderTagsRow(selectedTags, onSelectTag, items, searchQuery)
      }
      fetchData={(...args) =>
        fetchTagsData(company, tagNamespaces, selectedTags, usingTagsSearchShortcut, ...args)
      }
      onDropdownNavigateBack={usingTagsSearchShortcut ? undefined : onDropdownNavigateBack}
    />
  );
};

function renderTagsRow(
  selectedTags: ReadonlyArray<TagModel> | undefined,
  onSelectTag: HandlerOf<TagModel>,
  item: SearchTagsDropdownItem,
  searchQuery: string,
) {
  const isSearching = Boolean(searchQuery.length);
  return (
    <CellsRefinementOptionsSearchTagsDropdownRow
      selectedTags={selectedTags}
      onSelectTag={onSelectTag}
      isNotInteractive={false}
      shouldNest={!isSearching}
      item={item}
    />
  );
}

async function fetchTagsData(
  company: CompanyModel,
  tagNamespaces: ReadonlyArray<string>,
  selectedTags: ReadonlyArray<TagModel> | undefined,
  usingTagsSearchShortcut: boolean,
  paginationState: RefinementSearchDropdownPaginationState,
  items: ReadonlyArray<SearchTagsDropdownItem>,
  query: string,
  cancelToken: CancelToken,
): Promise<{
  data: ReadonlyArray<SearchTagsDropdownItem>;
  canRequestMore: boolean;
}> {
  const url = buildTagSearchUrl(company, query, {
    offset: paginationState.offset,
    namespaces: tagNamespaces,
    behaviors: [TagBehaviorsEnum.CAN_APPLY_FROM_LIST],
    limit: REFINEMENT_SEARCH_DROPDOWN_PAGINATION_LIMIT,
  });

  const tagSearch = await fetchApiAsync(ApiSearchTags, url, {cancelToken});

  const filteredTags = tagSearch.tags.filter(
    (tagsPath) => tagsPath.length !== 0 && !tagsPath[0].isMainInboxTag,
  );

  const previousAndCurrentItems = [
    ...items,
    ...filteredTags.map((tags) => ({type: SearchTagsDropdownItemTypes.UNSELECTED as const, tags})),
  ];
  const hasResults = paginationState.offset === 0 && previousAndCurrentItems.length > 0;

  const data = [
    ...(hasResults && usingTagsSearchShortcut && selectedTags !== undefined
      ? [{type: SearchTagsDropdownItemTypes.SELECTED as const, tags: selectedTags}]
      : []),
    ...(hasResults ? [{type: SearchTagsDropdownItemTypes.CATEGORY as const}] : []),
    ...previousAndCurrentItems,
  ];

  return {
    data,
    canRequestMore: tagSearch.tags.length === tagSearch.limit,
  };
}
